import { filter } from 'rxjs/operators';
import { getCookie } from '@utiljs/cookie';
import Modal from '@roo/roo/Modal';
import * as Ids from '@utils/ids';

import { getGrayStateByToken, GRAY_AB_TOKEN } from '@utils/grayAB';
import { UnprocessedOrderType } from '@business/pages/OrderManage/shared/types';

import {
    masterMessageHub as messageHub,
    PushCode,
    getWmPoiId,
    canBeHandledByCurrentSinglePoi,
    canBeHandledByCurrentPoi,
    currentLoginWmPoiId,
} from '@services/messageHub/instance';

import { getEnv, ENV } from '@utils/getEnv';
import request from '@utils/axiosWrapper';
import * as LX from '@utils/lxWrapper';
import { getStorage } from '@utils/localStorage';

import {
    addOrderNotification,
    addWeakNotification,
    addStrongNotification,
    MessageNotification,
} from '../connection/eventHandler/pushNotification';
import { UnifiedBusinessMessageNoticeData } from '../connection/push/PushMessage';
import { reportUnifiedBusinessMessageNoticeRead } from '../connection/push/pushMessageReceipt';
import bellService from '../service/bell';

import {
    orderPrepCountUpdateSubject,
    orderDeliveryCountUpdateSubject,
    orderProcessCountUpdateSubject,
    orderAllCountUpdateDelaySubject,
} from './subjects';

const pushToken = getCookie('pushToken');

/**
 * 初始化（除 order、phfOrder、poi 类别以外的） push 任务
 */
export default () => {
    // Code:11 活动通知
    messageHub.getPushSource(PushCode.ACTIVITY_NOTICE_11).subscribe(message => {
        addWeakNotification({
            title: '活动通知',
            content: message.message,
            wmPoiId: getWmPoiId(message),
            wmEMsgTraceId: message.wmEMsgTraceId,
            wmEPoiMsgId: message.wmEPoiMsgId,
        });
    });

    // Code:18 门店业务经理变更提醒
    messageHub.getPushSource(PushCode.BD_CHANGED_18).subscribe(message => {
        addWeakNotification({
            title: '业务经理变更',
            content: message.message,
            wmPoiId: getWmPoiId(message),
            wmEMsgTraceId: message.wmEMsgTraceId,
            wmEPoiMsgId: message.wmEPoiMsgId,
        });
    });

    // Code:22 商家菜品同步通知提醒
    messageHub.getPushSource(PushCode.PRODUCT_SYNC_NOTICE_22).subscribe(message => {
        addWeakNotification({
            title: '菜品同步通知',
            content: message.message,
            wmPoiId: getWmPoiId(message),
            wmEMsgTraceId: message.wmEMsgTraceId,
            wmEPoiMsgId: message.wmEPoiMsgId,
        });
    });

    // Code:25 评价申诉结果通知
    messageHub.getPushSource(PushCode.COMMENT_REPORT_RESULT_25).subscribe(message => {
        addWeakNotification({
            title: message.title,
            content: message.message,
            href: `${window.CommConstants.eappBaseUrl}/frontweb/userComment/commentAppearDetail?id=${message.comment_id}&poiId=${message.wmPoiId}`,
            wmPoiId: getWmPoiId(message),
            wmEMsgTraceId: message.wmEMsgTraceId,
            wmEPoiMsgId: message.wmEPoiMsgId,
        });
    });

    // Code:58 智能客服入口状态文案变更
    messageHub
        .getPushSource(PushCode.AI_CS_ENTRANCE_TEXT_CHANGE_58)
        .pipe(filter(message => canBeHandledByCurrentSinglePoi(message)))
        .subscribe(message => {
            let entryText = '';
            if (message.scriptShow === '1' && message.scriptValue) {
                entryText = message.scriptValue;
            } else if (message.textShow === '1' && message.textValue) {
                entryText = message.textValue;
            }
            const entryDiv = document.getElementById('customer-service-new');
            if (entryDiv) {
                const textSpan = entryDiv.getElementsByTagName('span');
                if (textSpan[0]) {
                    if (entryText !== '') {
                        textSpan[0].style.display = '';
                        textSpan[0].textContent = entryText;
                    } else {
                        textSpan[0].style.display = 'none';
                        textSpan[0].textContent = '';
                    }
                }
            }
        });

    // Code:101 特殊天气和节日提醒
    messageHub.getPushSource(PushCode.WEATHER_HOLIDAY_REMIND_101).subscribe(message => {
        addWeakNotification({
            title: message.title,
            content: message.message,
            wmPoiId: getWmPoiId(message),
            wmEMsgTraceId: message.wmEMsgTraceId,
            wmEPoiMsgId: message.wmEPoiMsgId,
        });
    });

    // Code:102 评价提醒
    messageHub.getPushSource(PushCode.COMMENT_REMIND_102).subscribe(message => {
        addWeakNotification({
            title: '评价提醒',
            content: message.message,
            href: `${window.CommConstants.eappBaseUrl}/frontweb/userComment?rate=-1&reply=-1&context=-1&commentKeyWord=-1`,
            wmPoiId: getWmPoiId(message),
            wmEMsgTraceId: message.wmEMsgTraceId,
            wmEPoiMsgId: message.wmEPoiMsgId,
        });
    });

    // Code:119 系统异常提醒
    messageHub.getPushSource(PushCode.SYSTEM_EXCEPTION_NOTICE_119).subscribe(() => {
        request
            .get<{ title: string; content: string; id: string | number } | null>(
                '/index/unprocessed/r/ErrorMsgInfo',
            )
            .then(res => {
                if (res) {
                    if (res.content) {
                        // eslint-disable-next-line waimaie/no-direct-access-localstorage
                        const exceptionType = localStorage.getItem('exceptionType');
                        addWeakNotification({
                            title: '系统异常提醒',
                            content: res.content,
                            wmEPoiMsgId: 'exceptionInfo',
                            wmEMsgTraceId: 'exceptionInfo',
                        });

                        if (!(exceptionType && exceptionType === `${res.id}`)) {
                            // eslint-disable-next-line waimaie/no-direct-access-localstorage
                            localStorage.setItem('exceptionType', `${res.id}`);
                            Modal.confirm({
                                title: res.title,
                                children: res.content,
                                showConfirmButton: false,
                                cancelText: '我知道了',
                            });
                        }
                    }
                }
            });
    });

    // Code:120 意见反馈收到回复
    messageHub
        .getPushSource(PushCode.FEEDBACK_REPLY_120)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            addWeakNotification({
                title: message.title,
                content: message.message,
                wmEPoiMsgId: message.wmEPoiMsgId,
                wmEMsgTraceId: message.wmEMsgTraceId,
            });
        });

    // Code:999 通用通知提醒
    messageHub.getPushSource(PushCode.BUSINESS_MESSAGE_NOTICE_999).subscribe(message => {
        const showType = parseInt(message.showType, 10);

        if (showType === 40) {
            //接单提醒（确认订单提醒）
            if (canBeHandledByCurrentPoi(message)) {
                if (message.confirmPushToken !== pushToken) {
                    // 接单设备的pushToken不同于本机pushToken，则提示其他设备接单
                    addOrderNotification({
                        content: '您有订单已被其他设备接单',
                        href: getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER)
                            ? '/new_fe/orderbusiness#/order/unprocessed'
                            : '/new_fe/business#/order/unprocessed',
                        wmPoiId: message.showPoiName ? message.wmPoiId : undefined,
                    });

                    if (
                        message.isPreOrder === 'true' // 注意是字符串型的 true
                    ) {
                        bellService.playBell('preorderOther');
                    } else {
                        bellService.playBell('other');
                    }
                }
                /** 餐饮新形态灰度 */
                const UNIFIED_ORDER_GRAY = getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER);
                if (UNIFIED_ORDER_GRAY) {
                    const messageInfo = message
                        ? Object.assign(message, {
                              UpdateType: [
                                  UnprocessedOrderType.PrepMeal,
                                  UnprocessedOrderType.PrepLogistics,
                              ],
                          })
                        : message;
                    orderAllCountUpdateDelaySubject.next(messageInfo);
                } else {
                    orderPrepCountUpdateSubject.next(message);
                    orderDeliveryCountUpdateSubject.next(message);
                    orderProcessCountUpdateSubject.next(message);
                }
            }
        } else {
            let notification: MessageNotification | null = null;

            if (showType === 2 || (showType === 5 && canBeHandledByCurrentSinglePoi(message))) {
                // 各业务方通过 Push 服务接口发出的消息
                notification = {
                    title: message.title,
                    content: (message.message || '').replace(/\+/g, '').replace(/\n/g, '<br>'),
                    wmEPoiMsgId: message.wmEPoiMsgId,
                    wmEMsgTraceId: message.wmEMsgTraceId,
                };
            } else if (showType === 4) {
                // 运营人员在me后台通过消息触达页面发出的消息，具体又分为文字型(type=1)和链接型(type=2)
                const msgType = parseInt(message.type, 10);
                if (msgType === 2) {
                    let content = message.content || '';
                    // 线上环境强制 https
                    if (getEnv() === ENV.PRODUCTION) {
                        content = content.replace(/^http(?!s)/, 'https');
                    }
                    notification = {
                        title: '重要通知',
                        content: message.title,
                        wmEPoiMsgId: message.wmEPoiMsgId,
                        wmEMsgTraceId: message.wmEMsgTraceId,
                        href:
                            content.indexOf('http') === 0
                                ? content
                                : `/new_fe/business#/msgbox/detail?id=${message.noticeId}`,
                    };
                } else {
                    notification = {
                        title: message.title,
                        content: message.content,
                        wmEPoiMsgId: message.wmEPoiMsgId,
                        wmEMsgTraceId: message.wmEMsgTraceId,
                    };
                }

                notification!.wmPoiId = message.wmPoiId;
                notification!.noticeId = message.noticeId;
            }

            if (notification !== null) {
                if (message.alert === 'true') {
                    addStrongNotification(notification);
                } else if (message.weakRemind === 'true') {
                    const doc = new DOMParser().parseFromString(notification.content, 'text/html');
                    notification.content = doc.body.textContent || '';

                    addWeakNotification(notification);
                }
            }
        }
    });

    // Code:1010 统一触达消息
    messageHub
        .getPushSource<UnifiedBusinessMessageNoticeData>(
            PushCode.UNIFIED_BUSINESS_MESSAGE_NOTICE_1010,
        )
        .subscribe(message => {
            if (message.poiOnly === 1 && !canBeHandledByCurrentSinglePoi(message)) {
                return;
            }

            if (
                message.showInternalPush &&
                message.internalPushInfo.title &&
                message.internalPushInfo.content
            ) {
                addWeakNotification({
                    title: message.internalPushInfo.title,
                    content: message.internalPushInfo.content,
                    href:
                        currentLoginWmPoiId === '-1' &&
                        `${getWmPoiId(message)}` !== '-1' &&
                        (message.internalPushInfo.linkUrl || '').match(
                            /\/new_fe\/business#\/msgbox\/detail/,
                        )
                            ? undefined
                            : message.internalPushInfo.linkUrl,
                    wmPoiId: getWmPoiId(message),
                    noticeId: `${message.id}`,
                    wmEMsgTraceId: `${message.wmEMsgTraceId}`,
                    wmEPoiMsgId: `${message.wmEPoiMsgId}`,
                    templateName: message.templateName,
                    noticeType: '1',
                });
            }

            if (message.showDialog && message.dialogInfo.title && message.dialogInfo.content) {
                addStrongNotification({
                    title: message.dialogInfo.title,
                    content: message.dialogInfo.content,
                    href:
                        currentLoginWmPoiId === '-1' &&
                        `${getWmPoiId(message)}` !== '-1' &&
                        (message.dialogInfo.linkUrl || '').match(
                            /\/new_fe\/business#\/msgbox\/detail/,
                        )
                            ? undefined
                            : message.dialogInfo.linkUrl,
                    wmPoiId: getWmPoiId(message),
                    noticeId: `${message.id}`,
                    wmEMsgTraceId: `${message.wmEMsgTraceId}`,
                    wmEPoiMsgId: `${message.wmEPoiMsgId}`,
                    templateName: message.templateName,
                    noticeType: '2',
                    dialogType: message.dialogInfo.dialogType,
                    ...(message.dialogInfo.dialogType === 2
                        ? {
                              customizeDialogConfig: {
                                  leftBtnType: message.dialogInfo.leftBtnType,
                                  leftBtnConfig: {
                                      ...JSON.parse(message.dialogInfo.leftBtnConfig || '{}'),
                                      buttonText: message.dialogInfo.leftButtonText,
                                  },
                                  rightBtnType: message.dialogInfo.rightBtnType,
                                  rightBtnConfig: {
                                      ...JSON.parse(message.dialogInfo.rightBtnConfig || '{}'),
                                      buttonText: message.dialogInfo.rightButtonText,
                                  },
                                  utmContent: message.dialogInfo.utmContent,
                                  buttonCnt: message.dialogInfo.buttonCnt,
                              },
                          }
                        : {}),
                });
            }

            if (message.soundBroad) {
                if (canBeHandledByCurrentPoi(message)) {
                    const bellType = `unified-sound-broad-${message.templateName}-${message.soundInfo.linkUrl}`;

                    LX.reportMV('b_waimai_e_pfgnt5v7_mv', 'c_waimai_e_742h21bc', {
                        poi_id: currentLoginWmPoiId,
                        msgId: message.wmEPoiMsgId,
                        traceId: message.wmEMsgTraceId,
                        pushType: 7,
                    });

                    // 动态注册、播放铃声
                    bellService.registerBell({
                        key: bellType,
                        resourceUrl: message.soundInfo.linkUrl,
                        priority: message.soundInfo.priority || 70,
                    });
                    bellService.playBell(bellType);

                    reportUnifiedBusinessMessageNoticeRead({
                        noticeId: `${message.id}`,
                        wmEMsgTraceId: `${message.wmEMsgTraceId}`,
                        wmEPoiMsgId: `${message.wmEPoiMsgId}`,
                        templateName: message.templateName,
                        noticeType: '7',
                    });
                }
            }
        });

    // Code:2000 强刷客户端
    messageHub.getPushSource(PushCode.FORCE_CLIENT_REFRESH_2000).subscribe(() => {
        window.location.reload();
    });

    // Code:4000 IM新消息铃声动态提示（首条、两分钟、十分钟、自定义定时任务）
    messageHub.getPushSource(PushCode.IM_MESSAGE_REMIND_4000).subscribe(message => {
        const imRemindConfigKey = `imMessageRemind_${message.pushType}`;
        const localConfig = getStorage(imRemindConfigKey);
        let shouldPlay: boolean;

        const typeIgnore = ['21', '22', '23'];

        if (localConfig === null || localConfig === undefined || localConfig === 'true') {
            shouldPlay = true;
        } else {
            shouldPlay = false;
        }

        if (typeIgnore.indexOf(`${message.pushType}`) >= 0) {
            shouldPlay = !shouldPlay;
        }

        if (message.remindScope === '1') {
            // 只在当前门店下播报
            if (Ids.getWmPoiId() !== `${message.wmPoiId}`) {
                shouldPlay = false;
            }
        } else if (message.remindScope === '2') {
            // 在当前门店和总店播报
            if (Ids.getWmPoiId() !== `${message.wmPoiId}` && Ids.getWmPoiId() !== '-1') {
                shouldPlay = false;
            }
        }

        if (shouldPlay) {
            const bellType = `im-remind-${message.soundUrl}`;
            // 动态注册、播放铃声
            bellService.registerBell({
                key: bellType,
                resourceUrl: message.soundUrl,
                priority: 70,
            });
            bellService.playBell(bellType);

            LX.reportMC('b_waimai_e_lebwcqn6_mc', 'c_9qz7mwbm', {
                // eslint-disable-next-line @typescript-eslint/camelcase
                bell_type: message.pushType,
                soundname: message.soundUrl,
                // eslint-disable-next-line @typescript-eslint/camelcase
                session_type: message.sessionType,
                // eslint-disable-next-line @typescript-eslint/camelcase
                session_id: message.sessionId || message.msgTraceId,
            });
        }
    });

    // Code:5000 规则中心预警通知（收到各业务系统的预警信息后，给预警商家发送消息）
    messageHub.getPushSource(PushCode.RULE_CENTER_WARNING_5000).subscribe(message => {
        const isSoundRemind = parseInt(message.is_sound_remind, 10) === 1;
        const isMessageRemind = parseInt(message.is_message_remind, 10) === 1;
        const pushMessageType = parseInt(message.push_message_type, 10);

        if (isSoundRemind && canBeHandledByCurrentSinglePoi(message)) {
            if (pushMessageType === 1) {
                bellService.playBell('breakRule');
            } else if (pushMessageType === 2) {
                bellService.playBell('earlyWarning');
            }
        }

        if (isMessageRemind && canBeHandledByCurrentPoi(message)) {
            LX.reportMV('b_waimai_e_vrriny3z_mv', 'c_waimai_e_n4g84fia');
            addWeakNotification({
                title: message.title,
                content: message.message.replace(/\+/g, ' '),
                href: message.link_url,
                wmPoiId: message.wmPoiId,
                linkText: message.link_text,
                readReceiptConfig: {
                    cid: 'c_waimai_e_n4g84fia',
                    mc: 'b_waimai_e_vrriny3z_mc',
                },
                wmEMsgTraceId: message.wmEMsgTraceId,
                wmEPoiMsgId: message.wmEPoiMsgId,
            });
        }
    });

    messageHub.getPushSource(PushCode.RESEND_MEAL_CANCEL_3016).subscribe(message => {
        bellService.playBell('busongCancel');
        const messageInfo = JSON.parse(message.extraParams || '{}');
        console.log('message', messageInfo, '我是 3016');
        Modal.confirm({
            title: '跑腿补送已取消',
            children: messageInfo.message,
            showConfirmButton: false,
            cancelText: '我知道了',
        });
    });
};
